import { Col, Row } from "react-grid-system";
import { paths } from "../../lib/router";
import Container from "../common/Container";
import toolStyles from "../../sharedStyles/Tool.module.css";
import styles from "./Results.module.css";
import classNames from "classnames";
import { toolTypeToName } from "../AccountDetails/utils";
import { ToolState, ToolType } from "../AccountDetails";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import StyledButton from "../common/buttons/StyledButton";
import CustomerServiceContent from "./CustomerServiceContent";
import PaymentContent from "./PaymentContent";
import NoPaymentContent from "./NoPaymentContent";
import {
  ResultDataByTab,
  ResultQuickClickOption,
  TabOption,
} from "./resultTypes";
import {
  CallContext,
  Channel,
  DateString,
  GetAccountDetailsResponse,
  MessagesResponse,
  ResultOption,
  ResultsDropdownsByChannel,
  ValidationErrors,
} from "../../lib/api/models";
import {
  useGetResultOptions,
  useSubmitResultCsr,
  useSubmitResultNoPayment,
  useSubmitResultPayment,
} from "../../lib/api/queries";
import { useChannelState } from "../../lib/channelContext";
import ToolLoadError from "../common/ToolLoadError";
import Spinner from "../common/Spinner";
import { getDisplayError, getDisplayMessages } from "../../lib/api/endpoints";
import ContactFieldset from "./ContactFieldset";
import PermissionToDiscussFieldset from "./PermissionToDiscussFieldset";
import { setValidationErrorsAndDisplayGeneralErrors } from "../../lib/validation";
import { ChannelMapToFull } from "../../lib/channelUtil";
import {
  getCallBackDateMinMax,
  getPaymentDateMinMax,
  getPromiseDateMinMax,
  getResultActionEntry,
  getNoPaymentWorkDateDefault,
  getNoPaymentWorkDateMinMax,
  getResultNote,
  getWorkDateMinMax,
  paymentMethodRequiresCallBack,
} from "./resultUtils";
import {
  convertDateStringToMonthDayYearFormat,
  dollarStringToCents,
} from "../../lib/formatting";
import { useNavigate } from "react-router-dom";
import ResultConflictModal from "./ResultConflictModal";
import ConfirmationModal from "../common/modals/ConfirmationModal";
import RoundButton from "../common/buttons/RoundButton";
import DebtDisclosureRequiredModal from "../DebtDisclosureRequiredModal";
import WarningModal from "../common/modals/WarningModal/WarningModal";
import { useDialerPullState } from "../../lib/dialerPullContext";

const strings = {
  csr: "CSR",
  payment: "Payment",
  noPayment: "No Payment",

  clear: "Clear",
  completeCall: "Complete Call",
  ok: "OK",
  cancel: "Cancel",

  callCompleted: "Call Completed",
  callCompletedMessage:
    "You have successfully submitted your result for this call.",

  clearForm: "Clear Form",
  clearMessage:
    "Are you sure you want to clear all your selections on this form?",

  a0069:
    "A_0069 - You have selected a payment that requires a confirm # to be written to the VerifiedPayment database. Is this a verified payment?",
  confirmationNumberMissingModalLabel: "Confirmation Number Missing Modal",
  yes: "Yes, Return and Enter Confirm #",
  no: "No, Complete Call without\nupdating VerifiedPayment database",

  a0114: (minDate: DateString, maxDate: DateString) =>
    `A_0114 - You must enter a valid Payment Dt between ${convertDateStringToMonthDayYearFormat(minDate)} and ${convertDateStringToMonthDayYearFormat(maxDate)}. This is the date that the contact will DO the action to complete the promise of the selected method.`,
  a0078: (minDate: DateString, maxDate: DateString) =>
    `A_0078 - You must enter a valid Promise Dt between ${convertDateStringToMonthDayYearFormat(minDate)} and ${convertDateStringToMonthDayYearFormat(maxDate)}. This is the date the payment should post to the credit card account.`,
  a0334: (minDate: DateString, maxDate: DateString) =>
    `A_0334 - You must enter a valid Work Dt between ${convertDateStringToMonthDayYearFormat(minDate)} and ${convertDateStringToMonthDayYearFormat(maxDate)}. The account is to be scheduled to be worked on this date.`,
  a0098: (minDate: DateString, maxDate: DateString) =>
    `A_0098 - You must enter a valid Call Back Dt between ${convertDateStringToMonthDayYearFormat(minDate)} and ${convertDateStringToMonthDayYearFormat(maxDate)}. The Call Back Dt is the date the contact will call to complete the method.`,

  a0071: (minDate: DateString, maxDate: DateString) =>
    `A_0071 - You must enter a valid Work Dt between ${convertDateStringToMonthDayYearFormat(minDate)} and ${convertDateStringToMonthDayYearFormat(maxDate)}. The account is to be scheduled to be worked on this date.`,
  a0057: "A_0057 - Please enter a dollar amount between 0.01 and 30,000.00.",
};

const options: { id: TabOption; label: string }[] = [
  { id: "CSR", label: strings.csr },
  { id: "PAYMENT", label: strings.payment },
  { id: "NOPAYMENT", label: strings.noPayment },
];

type Props = {
  className?: string;
  dwbuid: string;
  referenceId?: string;
  toolId: string;
  activeTools: ToolState[];
  onDisplayGeneralErrors: (error: unknown) => void;
  onSetToolInProgress: (id: string, inProgress: boolean) => void;
  onCloseTool: (id: string) => void;
  setIsResultComplete: React.Dispatch<React.SetStateAction<boolean>>;
  isDebtDisclosureBlocking: boolean;
  onOpenDebtDisclosure: () => void;
  nextCallContext: CallContext | null;
  accountDetails?: GetAccountDetailsResponse | null;
  onSubmitNote: () => Promise<void>;
  isNoteSubmitting: boolean;
};

type ContactPersonByTab = Record<TabOption, ResultOption | null>;

function isContactPersonPermissionToDiscuss(cp: ResultOption | null) {
  return cp?.id === "CM" || cp?.id === "MERCHANT";
}

function isSubmitDisabled({
  tab,
  channel,
  data,
  resultOptions,
  contactPerson,
  permissionToDiscuss,
}: {
  tab: TabOption;
  channel: Channel;
  data: ResultDataByTab;
  resultOptions?: ResultsDropdownsByChannel;
  contactPerson: ContactPersonByTab;
  permissionToDiscuss: boolean | null;
}): boolean {
  if (!resultOptions) {
    return true;
  }
  switch (tab) {
    case "CSR":
      return !!(
        (resultOptions.csrOptions.contactPerson &&
          permissionToDiscuss === null) ||
        (resultOptions.csrOptions.contactPerson &&
          contactPerson.CSR === null) ||
        (resultOptions.csrOptions.contactPerson &&
          contactPerson.CSR === null) ||
        (data.CSR.activities.length === 0 && data.CSR.transfer === null)
      );
    case "PAYMENT":
      return !!(
        (resultOptions.paymentOptions.actionCode &&
          data.PAYMENT.actionCode === null) ||
        !data.PAYMENT.paymentAmount ||
        (channel === "INB" &&
          resultOptions.paymentOptions.inboundCallReason &&
          data.PAYMENT.inboundReason === null) ||
        (resultOptions.paymentOptions.paymentMethod &&
          data.PAYMENT.paymentMethod === null) ||
        (channel === "TRN" &&
          resultOptions.paymentOptions.transferSource &&
          data.PAYMENT.transferSource === null) ||
        !data.PAYMENT.paymentDate ||
        !data.PAYMENT.promiseDate ||
        !data.PAYMENT.workDate ||
        (paymentMethodRequiresCallBack(data.PAYMENT.paymentMethod) &&
          !data.PAYMENT.callBackDate) ||
        (resultOptions.paymentOptions.contactPerson &&
          contactPerson.PAYMENT === null)
      );
    case "NOPAYMENT":
      return !!(
        (resultOptions.noPaymentOptions.actionCode &&
          data.NOPAYMENT.actionCode === null) ||
        (channel === "COR" &&
          resultOptions.noPaymentOptions.correspondenceReason &&
          data.NOPAYMENT.correspondenceReason === null) ||
        (channel === "INB" &&
          resultOptions.noPaymentOptions.inboundCallReason &&
          data.NOPAYMENT.inboundReason === null) ||
        (channel === "TRN" &&
          resultOptions.noPaymentOptions.transferSource &&
          data.NOPAYMENT.transferSource === null) ||
        !data.NOPAYMENT.workDate ||
        (resultOptions.noPaymentOptions.contactPerson &&
          contactPerson.NOPAYMENT === null)
      );
  }
}

const p2dWrapThreshold = 3;

const initialResultState: ResultDataByTab = {
  CSR: {
    activities: [],
    transfer: null,
  },
  PAYMENT: {
    actionCode: null,
    paymentAmount: "",
    paymentMethod: null,
    confirmationNumber: "",
    transferSource: null,
    inboundReason: null,
    paymentDate: "",
    promiseDate: "",
    workDate: "",
    callBackDate: "",
  },
  NOPAYMENT: {
    actionCode: null,
    contactDetail: null,
    contactPerson: null,
    correspondenceReason: null,
    inboundReason: null,
    other: null,
    transferSource: null,
    actionNote: "",
    workDate: getNoPaymentWorkDateDefault(),
  },
};

export default function Results({
  className,
  dwbuid,
  referenceId,
  toolId,
  onDisplayGeneralErrors,
  onSetToolInProgress,
  onCloseTool,
  activeTools,
  setIsResultComplete,
  isDebtDisclosureBlocking,
  onOpenDebtDisclosure,
  nextCallContext,
  accountDetails,
  onSubmitNote,
  isNoteSubmitting,
}: Props) {
  const blockReload = useRef<boolean>(false);

  const navigation = useNavigate();

  const { channel } = useChannelState();
  const { callContext, setCallContext } = useDialerPullState();

  const [isDebtDisclosureOpen, setIsDebtDisclosureOpen] = useState(false);
  const [isConflictOpen, setIsConflictOpen] = useState(false);
  const [isCompleteOpen, setIsCompleteOpen] = useState(false);
  const [isClearOpen, setIsClearOpen] = useState(false);

  const [selected, setSelected] = useState<TabOption>("CSR");

  const [contactPerson, setContactPerson] = useState<ContactPersonByTab>({
    CSR: null,
    PAYMENT: null,
    NOPAYMENT: null,
  });

  // Only used for CSR tab
  const [permissionToDiscuss, setPermissionToDiscuss] = useState<
    boolean | null
  >(null);

  // Only used for NOPAYMENT tab
  const [quickClickValue, setQuickClickValue] = useState("");
  const [quickClick, setQuickClick] = useState<ResultQuickClickOption | null>(
    null,
  );

  const [validationMessages, setValidationMessages] =
    useState<ValidationErrors>();

  const [submitMessages, setSubmitMessages] = useState("");

  const inProgressTools = useMemo(
    () =>
      activeTools.filter((i) => i.inProgress && i.type !== ToolType.Results),
    [activeTools],
  );

  const {
    data: resultOptions,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetResultOptions(channel);

  const { mutateAsync: submitResultCsr, isLoading: isCsrSubmitLoading } =
    useSubmitResultCsr();

  const {
    mutateAsync: submitResultPayment,
    isLoading: isPaymentSubmitLoading,
  } = useSubmitResultPayment();

  const {
    mutateAsync: submitResultNoPayment,
    isLoading: isNoPaymentSubmitLoading,
  } = useSubmitResultNoPayment();

  const [data, setData] = useState<ResultDataByTab>(
    callContext
      ? {
          ...initialResultState,
          NOPAYMENT: {
            ...initialResultState.NOPAYMENT,
            actionNote: callContext.phoneNumber.slice(6),
          },
        }
      : initialResultState,
  );

  const maintainAllDirtyState = useCallback(
    (dirty: boolean) => {
      blockReload.current = dirty;
      onSetToolInProgress(toolId, dirty);
    },
    [onSetToolInProgress, toolId],
  );

  const [
    showConfirmationNumberMissingModal,
    setShowConfirmationNumberMissingModal,
  ] = useState(false);

  useEffect(() => {
    switch (selected) {
      case "CSR":
        maintainAllDirtyState(
          contactPerson.CSR !== null ||
            permissionToDiscuss !== null ||
            data.CSR.activities.length > 0 ||
            data.CSR.transfer !== null,
        );
        break;
      case "PAYMENT":
        maintainAllDirtyState(
          contactPerson.PAYMENT !== null ||
            data.PAYMENT.actionCode !== null ||
            data.PAYMENT.paymentAmount !== "" ||
            data.PAYMENT.confirmationNumber !== "" ||
            data.PAYMENT.inboundReason !== null ||
            data.PAYMENT.paymentDate !== "" ||
            data.PAYMENT.paymentMethod !== null ||
            data.PAYMENT.promiseDate !== "" ||
            data.PAYMENT.transferSource !== null ||
            data.PAYMENT.workDate !== "",
        );
        break;
      case "NOPAYMENT":
        maintainAllDirtyState(
          contactPerson.NOPAYMENT !== null ||
            data.NOPAYMENT.actionCode !== null ||
            data.NOPAYMENT.contactDetail !== null ||
            data.NOPAYMENT.correspondenceReason !== null ||
            data.NOPAYMENT.inboundReason !== null ||
            data.NOPAYMENT.other !== null ||
            data.NOPAYMENT.transferSource !== null ||
            data.NOPAYMENT.workDate !== "",
        );
        break;
    }
  }, [
    selected,
    contactPerson,
    permissionToDiscuss,
    data,
    maintainAllDirtyState,
  ]);

  const {
    "PAYMENT.paymentDate": paymentPaymentDate,
    "PAYMENT.promiseDate": paymentPromiseDate,
    "PAYMENT.workDate": paymentWorkDate,
    "PAYMENT.callBackDate": paymentCallBackDate,
    "PAYMENT.paymentAmount": paymentAmount,
    "NOPAYMENT.workDate": noPaymentWorkDate,
    ...restValidationMessages
  } = validationMessages ?? {};

  const paymentValidationMessages: ValidationErrors = {
    paymentAmount: paymentAmount,
    paymentDate: paymentPaymentDate,
    promiseDate: paymentPromiseDate,
    workDate: paymentWorkDate,
    callBackDate: paymentCallBackDate,
  };

  const noPaymentValidationMessages: ValidationErrors = {
    workDate: noPaymentWorkDate,
  };

  function onConflictOrSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (inProgressTools.length > 0) {
      setIsConflictOpen(true);
      return;
    }
    if (isDebtDisclosureBlocking) {
      setIsDebtDisclosureOpen(true);
      return;
    }
    onSubmit();
  }

  async function onSubmit(skipConfirmationNumber?: boolean) {
    if (
      isSubmitDisabled({
        tab: selected,
        channel,
        data,
        resultOptions,
        contactPerson,
        permissionToDiscuss,
      })
    ) {
      return;
    }

    let validatedPaymentAmountCents: number = 0;
    if (selected === "PAYMENT") {
      try {
        validatedPaymentAmountCents = dollarStringToCents(
          data.PAYMENT.paymentAmount,
          strings.payment,
        );
      } catch (err) {
        if (err instanceof Error) {
          setValidationMessages({
            "PAYMENT.paymentAmount": [err.message],
          });
          return;
        }
      }

      if (
        data.PAYMENT.paymentMethod?.id === "WIRE" &&
        (validatedPaymentAmountCents < 1 ||
          validatedPaymentAmountCents > 3000000)
      ) {
        setValidationMessages({ "PAYMENT.paymentAmount": [strings.a0057] });
        return;
      }
    }

    // Payment date validation
    if (selected === "PAYMENT") {
      const validationErrors: ValidationErrors = {};

      const minMaxPaymentDate = getPaymentDateMinMax(data.PAYMENT);
      if (
        minMaxPaymentDate.min &&
        minMaxPaymentDate.max &&
        data.PAYMENT.paymentDate &&
        (data.PAYMENT.paymentDate < minMaxPaymentDate.min ||
          data.PAYMENT.paymentDate > minMaxPaymentDate.max)
      ) {
        validationErrors["PAYMENT.paymentDate"] = [
          strings.a0114(minMaxPaymentDate.min, minMaxPaymentDate.max),
        ];
      }

      const minMaxPromiseDate = getPromiseDateMinMax(data.PAYMENT);
      if (
        minMaxPromiseDate.min &&
        minMaxPromiseDate.max &&
        data.PAYMENT.promiseDate &&
        (data.PAYMENT.promiseDate < minMaxPromiseDate.min ||
          data.PAYMENT.promiseDate > minMaxPromiseDate.max)
      ) {
        validationErrors["PAYMENT.promiseDate"] = [
          strings.a0078(minMaxPromiseDate.min, minMaxPromiseDate.max),
        ];
      }

      const minMaxWorkDate = getWorkDateMinMax(data.PAYMENT);
      if (
        minMaxWorkDate.min &&
        minMaxWorkDate.max &&
        data.PAYMENT.workDate &&
        (data.PAYMENT.workDate < minMaxWorkDate.min ||
          data.PAYMENT.workDate > minMaxWorkDate.max)
      ) {
        validationErrors["PAYMENT.workDate"] = [
          strings.a0334(minMaxWorkDate.min, minMaxWorkDate.max),
        ];
      }

      const minMaxCallBackDate = getCallBackDateMinMax(data.PAYMENT);
      if (
        minMaxCallBackDate.min &&
        minMaxCallBackDate.max &&
        data.PAYMENT.callBackDate &&
        (data.PAYMENT.callBackDate < minMaxCallBackDate.min ||
          data.PAYMENT.callBackDate > minMaxCallBackDate.max)
      ) {
        validationErrors["PAYMENT.callBackDate"] = [
          strings.a0098(minMaxCallBackDate.min, minMaxCallBackDate.max),
        ];
      }

      if (Object.keys(validationErrors).length > 0) {
        setValidationMessages(validationErrors);
        return;
      }

      if (!data.PAYMENT.confirmationNumber && !skipConfirmationNumber) {
        setShowConfirmationNumberMissingModal(true);
        return;
      }
    }

    // No Payment date validation
    if (selected === "NOPAYMENT") {
      const validationErrors: ValidationErrors = {};

      const minMaxWorkDate = getNoPaymentWorkDateMinMax();
      if (
        minMaxWorkDate.min &&
        minMaxWorkDate.max &&
        data.NOPAYMENT.workDate &&
        (data.NOPAYMENT.workDate < minMaxWorkDate.min ||
          data.NOPAYMENT.workDate > minMaxWorkDate.max)
      ) {
        validationErrors["NOPAYMENT.workDate"] = [
          strings.a0071(minMaxWorkDate.min, minMaxWorkDate.max),
        ];
      }

      if (Object.keys(validationErrors).length > 0) {
        setValidationMessages(validationErrors);
        return;
      }
    }

    try {
      await onSubmitNote();
    } catch (err) {
      onDisplayGeneralErrors(err);
      return;
    }

    try {
      let resp: MessagesResponse;
      switch (selected) {
        case "CSR":
          resp = await submitResultCsr({
            dwbuid,
            referenceId,
            channel: ChannelMapToFull[channel],
            activities: data.CSR.activities.map((item) => item.id),
            contactType: contactPerson.CSR?.id ?? "",
            permissionToDiscuss: permissionToDiscuss || false,
            resultNote: getResultNote({
              tab: selected,
              data: {
                ...data.CSR,
                contactPerson: contactPerson.CSR,
                permissionToDiscuss,
              },
            }),
            transferType: data.CSR.transfer?.id ?? null,
            callContextId: callContext?.id ?? null,
          });
          break;
        case "PAYMENT":
          resp = await submitResultPayment({
            dwbuid,
            referenceId,
            channel: ChannelMapToFull[channel],
            actionCode: data.PAYMENT.actionCode?.id ?? "",
            actionEntry: getResultActionEntry({
              tab: selected,
              data: data.PAYMENT,
            }),
            amountDueCents: accountDetails?.amountDueCents,
            confirmationNumber: data.PAYMENT.confirmationNumber,
            contactType: contactPerson.PAYMENT?.id ?? "",
            inboundReason: data.PAYMENT.inboundReason?.id ?? null,
            paymentAmountCents: validatedPaymentAmountCents,
            paymentDate: data.PAYMENT.paymentDate,
            paymentDueDate: accountDetails?.paymentDueDate,
            paymentMethodId: data.PAYMENT.paymentMethod?.id ?? "",
            promiseDate: data.PAYMENT.promiseDate,
            resultNote: getResultNote({
              tab: selected,
              data: { ...data.PAYMENT, contactPerson: contactPerson.PAYMENT },
            }),
            transferSource: data.PAYMENT.transferSource?.id ?? null,
            workDate: data.PAYMENT.workDate,
            callBackDate: data.PAYMENT.callBackDate
              ? data.PAYMENT.callBackDate
              : null,
            callContextId: callContext?.id ?? null,
          });
          break;
        case "NOPAYMENT":
          resp = await submitResultNoPayment({
            dwbuid,
            referenceId,
            channel: ChannelMapToFull[channel],
            actionNote: data.NOPAYMENT.actionNote,
            actionCode: data.NOPAYMENT.actionCode?.id ?? "",
            actionEntry: getResultActionEntry({
              tab: selected,
              data: {
                ...data.NOPAYMENT,
                contactPerson: contactPerson.NOPAYMENT,
              },
            }),
            contactType: contactPerson.NOPAYMENT?.id ?? "",
            inboundReason: data.NOPAYMENT.inboundReason?.id ?? null,
            transferSource: data.NOPAYMENT.transferSource?.id ?? null,
            workDate: data.NOPAYMENT.workDate,
            contactDetailType: data.NOPAYMENT.contactDetail?.id ?? null,
            correspondenceReason:
              data.NOPAYMENT.correspondenceReason?.id ?? null,
            otherId: data.NOPAYMENT.other?.id ?? null,
            callContextId: callContext?.id ?? null,
          });
          break;
      }
      maintainAllDirtyState(false);
      setIsResultComplete(true);
      setIsCompleteOpen(true);
      setSubmitMessages(getDisplayMessages(resp).message);
    } catch (err: unknown) {
      setValidationErrorsAndDisplayGeneralErrors(
        err,
        setValidationMessages,
        onDisplayGeneralErrors,
      );
    }
  }

  function renderInnerContent() {
    if (isLoading || isError) {
      return null;
    }

    switch (selected) {
      case "CSR":
        return (
          <CustomerServiceContent
            data={data[selected]}
            setData={setData}
            options={resultOptions.csrOptions}
            contactPerson={contactPerson[selected]}
            permissionToDiscuss={permissionToDiscuss}
          />
        );
      case "PAYMENT":
        return (
          <PaymentContent
            dwbuid={dwbuid}
            data={data[selected]}
            setData={setData}
            options={resultOptions.paymentOptions}
            contactPerson={contactPerson[selected]}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            validationMessages={paymentValidationMessages}
            setValidationMessages={setValidationMessages}
            referenceId={referenceId}
          />
        );
      case "NOPAYMENT":
        return (
          <NoPaymentContent
            dwbuid={dwbuid}
            data={{ ...data[selected], contactPerson: contactPerson[selected] }}
            setData={setData}
            options={resultOptions.noPaymentOptions}
            quickClick={quickClick}
            setQuickClick={setQuickClick}
            quickClickValue={quickClickValue}
            setQuickClickValue={setQuickClickValue}
            setContactPerson={(cp) =>
              setContactPerson((d) => ({ ...d, NOPAYMENT: cp }))
            }
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            validationMessages={noPaymentValidationMessages}
            setValidationMessages={setValidationMessages}
            referenceId={referenceId}
          />
        );
    }
  }

  return (
    <div className={classNames(toolStyles.container, className)} id={toolId}>
      {isLoading ? (
        <Container>
          <Row
            className={classNames(
              toolStyles.header,
              toolStyles.headerWithDivider,
              styles.header,
            )}
          >
            <Col className={toolStyles.centerCol}>
              <h2 className={toolStyles.title}>
                {toolTypeToName(ToolType.Results)}
              </h2>
            </Col>
          </Row>
          <Row>
            <Col>
              <Spinner className={styles.spinner} />
            </Col>
          </Row>
        </Container>
      ) : isError ? (
        <Container>
          <Row
            className={classNames(
              toolStyles.header,
              toolStyles.headerWithDivider,
              styles.header,
            )}
          >
            <Col className={toolStyles.centerCol}>
              <h2 className={toolStyles.title}>
                {toolTypeToName(ToolType.Results)}
              </h2>
            </Col>
          </Row>
          <Row>
            <Col>
              <ToolLoadError
                toolName={toolTypeToName(ToolType.Results)}
                errorDescription={getDisplayError(error)?.message}
                onRetry={refetch}
              />
            </Col>
          </Row>
        </Container>
      ) : (
        <form onSubmit={onConflictOrSubmit} noValidate>
          <Container>
            <Row
              className={classNames(
                toolStyles.header,
                toolStyles.headerWithDivider,
                styles.header,
              )}
            >
              <Col md={1} className={toolStyles.centerCol}>
                <h2 className={toolStyles.title}>
                  {toolTypeToName(ToolType.Results)}
                </h2>
              </Col>
              <Col
                md={3}
                className={classNames(
                  styles.pillButtonContainer,
                  toolStyles.centerCol,
                )}
              >
                {options.map(({ id, label }) => (
                  <RoundButton
                    key={id}
                    className={styles.pillButton}
                    variant={selected === id ? "blue" : "grey"}
                    onClick={() => setSelected(id)}
                  >
                    {label}
                  </RoundButton>
                ))}
              </Col>
              <Col
                md={5}
                className={classNames({
                  [toolStyles.centerCol]: true,
                  [styles.headerFieldsetContainer]: true,
                  [styles.headerFieldsetContainerCompact]:
                    selected === "CSR" &&
                    (resultOptions.csrOptions.contactPerson?.length ?? 0) >
                      p2dWrapThreshold,
                })}
              >
                <ContactFieldset
                  hasPermissionToDiscuss={
                    selected === "CSR" &&
                    (resultOptions.csrOptions.contactPerson?.length ?? 0) > 0
                  }
                  selectedOption={contactPerson[selected]}
                  options={
                    (selected === "CSR"
                      ? resultOptions.csrOptions.contactPerson
                      : selected === "PAYMENT"
                        ? resultOptions.paymentOptions.contactPerson
                        : selected === "NOPAYMENT"
                          ? resultOptions.noPaymentOptions.contactPerson
                          : []) ?? []
                  }
                  onChange={(cp) => {
                    setContactPerson((v) => ({ ...v, [selected]: cp }));
                    if (selected === "CSR") {
                      if (isContactPersonPermissionToDiscuss(cp)) {
                        setPermissionToDiscuss(true);
                      } else {
                        setPermissionToDiscuss(null);
                      }
                    }
                  }}
                />
                {selected === "CSR" &&
                  resultOptions.csrOptions.contactPerson?.length && (
                    <PermissionToDiscussFieldset
                      variant={
                        (resultOptions.csrOptions.contactPerson?.length ?? 0) >
                        p2dWrapThreshold
                          ? "compact"
                          : "default"
                      }
                      value={permissionToDiscuss}
                      onChange={setPermissionToDiscuss}
                      disabled={isContactPersonPermissionToDiscuss(
                        contactPerson[selected],
                      )}
                    />
                  )}
              </Col>
              <Col
                md={3}
                className={classNames(
                  styles.buttonContainer,
                  toolStyles.centerCol,
                )}
              >
                <StyledButton
                  variant="secondary"
                  type="button"
                  onClick={() => setIsClearOpen(true)}
                  className={styles.clearButton}
                  disabled={
                    isCsrSubmitLoading ||
                    isPaymentSubmitLoading ||
                    isNoPaymentSubmitLoading ||
                    isNoteSubmitting
                  }
                >
                  {strings.clear}
                </StyledButton>
                <StyledButton
                  variant="primary"
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitDisabled({
                    tab: selected,
                    channel,
                    data,
                    resultOptions,
                    contactPerson,
                    permissionToDiscuss,
                  })}
                  isLoading={
                    isCsrSubmitLoading ||
                    isPaymentSubmitLoading ||
                    isNoPaymentSubmitLoading ||
                    isNoteSubmitting
                  }
                >
                  {strings.completeCall}
                </StyledButton>
              </Col>
            </Row>
            {renderInnerContent()}
            {restValidationMessages &&
              Object.values(restValidationMessages).length > 0 && (
                <div className={styles.error}>
                  {Object.values(restValidationMessages)
                    .map((m) => m.join(" "))
                    .join(" ")}
                </div>
              )}
          </Container>
        </form>
      )}
      <DebtDisclosureRequiredModal
        isOpen={isDebtDisclosureOpen}
        onRequestClose={() => {
          onOpenDebtDisclosure();
          setIsDebtDisclosureOpen(false);
        }}
      />
      <ResultConflictModal
        isOpen={isConflictOpen}
        inProgressTools={inProgressTools}
        onRequestClose={() => setIsConflictOpen(false)}
      />
      <ConfirmationModal
        isOpen={isCompleteOpen}
        onRequestClose={() => {
          if (nextCallContext && nextCallContext.dwbuid) {
            activeTools.forEach((tool) => onCloseTool(tool.id));
            navigation(paths.accountDetails(nextCallContext.dwbuid), {
              replace: true,
              state: { callContext: nextCallContext },
            });
          } else if (nextCallContext && !nextCallContext.dwbuid) {
            navigation(paths.accountSearch, {
              state: { callContext: nextCallContext },
            });
          } else {
            setCallContext(null);
            navigation(paths.accountSearch);
          }
        }}
        title={strings.callCompleted}
        message={submitMessages ? submitMessages : strings.callCompletedMessage}
        cancelText={strings.ok}
      />
      <ConfirmationModal
        isOpen={isClearOpen}
        onRequestClose={() => setIsClearOpen(false)}
        onConfirm={() => {
          setValidationMessages(undefined);
          setData((d) => ({ ...d, [selected]: initialResultState[selected] }));
          setContactPerson((d) => ({ ...d, [selected]: null }));
          if (selected === "CSR") {
            setPermissionToDiscuss(null);
          }
          if (selected === "NOPAYMENT") {
            setQuickClick(null);
            setQuickClickValue("");
          }
          setIsClearOpen(false);
        }}
        title={strings.clearForm}
        message={strings.clearMessage}
        cancelText={strings.cancel}
        confirmText={strings.clear}
      />
      <WarningModal
        modalLabel={strings.confirmationNumberMissingModalLabel}
        isOpen={showConfirmationNumberMissingModal}
        onRequestClose={() => setShowConfirmationNumberMissingModal(false)}
        onConfirm={() => {
          setShowConfirmationNumberMissingModal(false);
          onSubmit(true);
        }}
        body={<div className={styles.warningModalTitle}>{strings.a0069}</div>}
        closeText={strings.yes}
        confirmText={strings.no}
      />
    </div>
  );
}
